import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, ArrowRight, Check, Star, Mail, Phone, MapPin, Zap, Calendar, Target, MessageSquare, Settings, BarChart3, Link2, Bot, Users, Globe } from 'lucide-react';
import SignupFormModal from '../components/SignupFormModal';

const WHATSAPP_URL = `https://wa.me/447757233260?text=${encodeURIComponent(
  `Hi! I just submitted the contact form on your website. Looking forward to hearing from the Qorvix team.`
)}`;

export default function QorvixLanding() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const [scrolled, setScrolled] = useState(false);
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);
  const [modalButtonType, setModalButtonType] = useState('demo');
  const [isAnnualBilling, setIsAnnualBilling] = useState(false);
  const [contactFormData, setContactFormData] = useState({
    username: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isContactSubmitting, setIsContactSubmitting] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const features = [
    {
      title: "AI Messaging Bot",
      description: "Instantly reply to leads, qualify prospects, and route them to the right department - all with human-like AI",
      Icon: MessageSquare
    },
    {
      title: "Workflow Automation",
      description: "Automate daily tasks across WhatsApp, email, CRM, Google Sheets - without writing a single line of code",
      Icon: Settings
    },
    {
      title: "Smart Scheduling",
      description: "Automatically book calls, demos, or appointments. Sync with Google Calendar, Calendly, or HubSpot.",
      Icon: Calendar
    },
    {
      title: "Analytics Dashboard",
      description: "Track performance, monitor conversations, and optimize your automation strategy with real-time insights",
      Icon: BarChart3
    },
    {
      title: "CRM Integration",
      description: "Connect Airtable, HubSpot, Notion, or custom CRMs - all your leads and data sync automatically",
      Icon: Link2
    },
    {
      title: "Custom AI Prompt",
      description: "Train your AI assistant with your brand tone and FAQs - consistent, on-brand conversation every time.",
      Icon: Bot
    },
    {
      title: "Human Handoff",
      description: "When a lead needs a real person, Qorvix routes the chat instantly to your team - no context lost.",
      Icon: Users
    },
    {
      title: "Global Scalability",
      description: "Multilingual and timezone-aware - serve customers 24/7 across countries without lifting a finger.",
      Icon: Globe
    }
  ];


  const testimonials = [
    {
      name: "Emily Watson",
      role: "Head of Customer Success at Electromart UK",
      text: "We handle 500+ customer inquiries daily. Qorvix responds instantly to every single one. Our support team now focuses on complex issues while AI handles the routine. Game changer.",
      rating: 5
    },
    {
      name: "Sarah Mitchell",
      role: "Sales Director at Luxury Motors UK",
      text: "Since implementing Qorvix, we respond to every WhatsApp lead in under 30 seconds. Our conversion rate jumped 47% in the first month. It's like having a full sales team working 24/7.",
      rating: 5
    },
    {
      name: "James Harrison",
      role: "Operations Manager at AutoNation Birmingham",
      text: "The AI answers questions perfectly, books test drives automatically, and never misses a follow-up. Our showroom visits increased by 60%. Best investment we've made this year.",
      rating: 5
    }
  ];

  const topReasons = [
    {
      Icon: Zap,
      title: "Instant Lead Response",
      description: "Reply to new inquiries via WhatsApp or email within 30 seconds - 24/7"
    },
    {
      Icon: Calendar,
      title: "Automated Scheduling",
      description: "Book test drives or property viewings instantly, without back-and-forth messages."
    },
    {
      Icon: Target,
      title: "Serious Lead Filtering",
      description: "AI pre-qualifies buyers by budget, intent, and urgency before your team steps in."
    }
  ];
  const handleContactChange = (e) => {
    setContactFormData({
      ...contactFormData,
      [e.target.name]: e.target.value
    });
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsContactSubmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...contactFormData,
          timestamp: new Date().toISOString()
        })
      });

      if (response.ok) {
        setContactSuccess(true);
        // Force redirect to WhatsApp — navigation (not popup) is never blocked
        setTimeout(() => {
          window.location.href = WHATSAPP_URL;
        }, 1500);
      } else {
        alert('Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to submit. Please try again.');
    } finally {
      setIsContactSubmitting(false);
    }
  };
  return (
    <div className="min-h-screen bg-white font-['Outfit',sans-serif] text-gray-900">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&family=Space+Mono:wght@400;700&display=swap');
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(50px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.7;
          }
        }

        @keyframes gradient {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }
        
        .animate-fadeInUp {
          animation: fadeInUp 0.8s ease-out forwards;
        }
        
        .animate-slideInRight {
          animation: slideInRight 0.8s ease-out forwards;
        }
        
        .animate-pulse-slow {
          animation: pulse 3s ease-in-out infinite;
        }

        .gradient-bg {
          background: linear-gradient(-45deg, #FF6B35, #FF8C42, #FFA500, #FFB84D);
          background-size: 400% 400%;
          animation: gradient 15s ease infinite;
        }

        .glass-effect {
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        .text-gradient {
          background: linear-gradient(135deg, #FF6B35 0%, #FFA500 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .hover-lift {
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }

        .hover-lift:hover {
          transform: translateY(-8px);
          box-shadow: 0 20px 40px rgba(255, 107, 53, 0.2);
        }

        .feature-card {
          position: relative;
          overflow: hidden;
        }

        .feature-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 107, 53, 0.1), transparent);
          transition: left 0.5s;
        }

        .feature-card:hover::before {
          left: 100%;
        }
      `}</style>

      {/* Navigation */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2">
              <img
                src="/logo-qorvix.png"
                alt="Qorvix Logo"
                className="h-10 w-auto"
              />
            </Link>

            {/* Desktop Menu */}
            <div className="hidden lg:flex items-center space-x-8">
              <Link to="/" className="text-orange-500 transition-colors font-medium text-[15px]">Home</Link>
              <Link to="/about" className="text-gray-900 hover:text-orange-500 transition-colors font-medium text-[15px]">About</Link>
              <Link to="/how-it-works" className="text-gray-900 hover:text-orange-500 transition-colors font-medium text-[15px]">How it works</Link>
              <Link to="/portfolio" className="text-gray-900 hover:text-orange-500 transition-colors font-medium text-[15px]">Portfolio</Link>
              <div className="relative group">
                <button className="text-gray-900 hover:text-orange-500 transition-colors font-medium text-[15px] flex items-center gap-1">
                  Resources
                  <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
                    <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                {/* Dropdown Menu */}
                <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  <div className="py-2">
                    <a href="#documentation" className="block px-4 py-2.5 text-gray-700 hover:bg-gray-50 hover:text-orange-500 transition-colors">Documentation</a>
                    <a href="#integration" className="block px-4 py-2.5 text-gray-700 hover:bg-gray-50 hover:text-orange-500 transition-colors">Integration</a>
                    <Link to="/faq" className="block px-4 py-2.5 text-gray-700 hover:bg-gray-50 hover:text-orange-500 transition-colors">FAQs</Link>
                    <a href="#help-center" className="block px-4 py-2.5 text-gray-700 hover:bg-gray-50 hover:text-orange-500 transition-colors">Help center</a>
                    <a href="#roadmap" className="block px-4 py-2.5 text-gray-700 hover:bg-gray-50 hover:text-orange-500 transition-colors">Roadmap</a>
                  </div>
                </div>
              </div>
              <Link to="/contact" className="text-gray-900 hover:text-orange-500 transition-colors font-medium text-[15px]">Contact us</Link>
              <button
                onClick={() => {
                  setModalButtonType('automate');
                  setIsSignupModalOpen(true);
                }}
                className="bg-transparent border-2 border-orange-500 text-orange-500 px-6 py-2.5 rounded-lg hover:bg-orange-500 hover:text-white transition-all font-medium text-[15px]"
              >
                Automate Now
              </button>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden text-gray-900"
            >
              {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <div className="fixed inset-0 bg-black/20 backdrop-blur-sm" onClick={() => setMobileMenuOpen(false)}></div>
            <div className="fixed top-4 right-4 left-4 bg-white rounded-2xl shadow-2xl p-8 max-w-sm mx-auto">
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
              >
                <X size={24} />
              </button>
              <nav className="mt-4 space-y-6">
                <Link to="/" className="block text-lg font-medium text-gray-900 hover:text-orange-500 transition-colors" onClick={() => setMobileMenuOpen(false)}>Home</Link>
                <Link to="/about" className="block text-lg font-medium text-gray-900 hover:text-orange-500 transition-colors" onClick={() => setMobileMenuOpen(false)}>About</Link>
                <Link to="/how-it-works" className="block text-lg font-medium text-gray-900 hover:text-orange-500 transition-colors" onClick={() => setMobileMenuOpen(false)}>How it works</Link>
                <Link to="/portfolio" className="block text-lg font-medium text-gray-900 hover:text-orange-500 transition-colors" onClick={() => setMobileMenuOpen(false)}>portfolio</Link>
                <div>
                  <div className="flex items-center justify-between text-lg font-medium text-gray-900 mb-4">
                    <span>resources</span>
                    <svg width="12" height="8" viewBox="0 0 12 8" fill="none" className="mt-1">
                      <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <div className="ml-4 space-y-4">
                    <Link to="/documentation" className="block text-base text-gray-700 hover:text-orange-500 transition-colors" onClick={() => setMobileMenuOpen(false)}>Documentation</Link>
                    <Link to="/integrations" className="block text-base text-gray-700 hover:text-orange-500 transition-colors" onClick={() => setMobileMenuOpen(false)}>Integrations</Link>
                    <Link to="/faq" className="block text-base text-gray-700 hover:text-orange-500 transition-colors" onClick={() => setMobileMenuOpen(false)}>FAQs</Link>
                    <Link to="/help-center" className="block text-base text-gray-700 hover:text-orange-500 transition-colors" onClick={() => setMobileMenuOpen(false)}>Help center</Link>
                    <Link to="/roadmap" className="block text-base text-gray-700 hover:text-orange-500 transition-colors" onClick={() => setMobileMenuOpen(false)}>Roadmap</Link>
                  </div>
                </div>
                <Link to="/contact" className="block text-lg font-medium text-gray-900 hover:text-orange-500 transition-colors" onClick={() => setMobileMenuOpen(false)}>Contact us</Link>
                <button
                  onClick={() => {
                    setModalButtonType('demo');
                    setIsSignupModalOpen(true);
                    setMobileMenuOpen(false);
                  }}
                  className="w-full text-left text-lg font-medium text-gradient border-b-2 border-orange-500 pb-1"
                >
                  Start free demo
                </button>
              </nav>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 overflow-hidden">
        <div className="absolute inset-0 gradient-bg opacity-10"></div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="animate-fadeInUp">
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-tight mb-6">
                Let Qorvix Handle The Work That{' '}
                <span className="text-gradient">Slows You Down</span>
              </h1>
              <p className="text-lg md:text-xl text-gray-600 mb-8 leading-relaxed">
                We help modern businesses scale faster by automating repetitive tasks, lead response, and client follow-ups using AI. Save hours every week and turn conversations into conversions - automatically.
              </p>
              <button onClick={() => {
                setModalButtonType('demo');
                setIsSignupModalOpen(true);
                setMobileMenuOpen(false);  // if you have mobile menu
              }} className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-8 py-4 rounded-full text-lg font-semibold hover:shadow-2xl transform hover:scale-105 transition-all flex items-center gap-2 group">
                Automate now
                <ArrowRight className="group-hover:translate-x-1 transition-transform" size={20} />
              </button>
            </div>
            <div className="animate-slideInRight">
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1620712943543-bcc4688e7485?w=800&h=600&fit=crop"
                  alt="AI Automation Robot"
                  className="rounded-3xl shadow-2xl hover-lift"
                />
                <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-2xl shadow-xl">
                  <div className="text-3xl font-bold text-gradient">15,000+</div>
                  <div className="text-sm text-gray-600 font-medium">Leads Automated Daily</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Top Reasons Section */}
      <section className="py-20 px-4 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Top Reason To Chose <span className="text-gradient">Qorvix</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We build AI systems that makes your sales team faster, smarter and always available.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {topReasons.map((reason, index) => {
              const IconComponent = reason.Icon;
              return (
                <div
                  key={index}
                  className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-lg transition-all"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-blue-50 rounded-2xl flex items-center justify-center mb-6">
                    <IconComponent className="text-blue-600" size={36} strokeWidth={2} />
                  </div>
                  <h3 className="text-xl font-bold mb-3">{reason.title}</h3>
                  <p className="text-gray-600 leading-relaxed text-sm">{reason.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Built for Sales Teams Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Built for sales teams that{' '}
                <span className="text-gradient">move fast</span>
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Qorvix connects your WhatsApp, email, and CRM into one seamless automation flow. No missed leads. No forgotten follow-ups. Just consistent conversions.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="bg-orange-100 rounded-full p-2 mt-1">
                    <Check className="text-orange-600" size={20} />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Respond to every inquiry in seconds</h4>
                    <p className="text-gray-600">Never miss a potential customer again</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="bg-orange-100 rounded-full p-2 mt-1">
                    <Check className="text-orange-600" size={20} />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Book viewings or test drives automatically</h4>
                    <p className="text-gray-600">Seamless scheduling without the back-and-forth</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="bg-orange-100 rounded-full p-2 mt-1">
                    <Check className="text-orange-600" size={20} />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Sync leads to Airtable, HubSpot, or Sheets</h4>
                    <p className="text-gray-600">All your data in one place, always up to date</p>
                  </div>
                </div>
              </div>
              <button onClick={() => {
                setModalButtonType('automate');
                setIsSignupModalOpen(true);
              }} className="mt-8 border-2 border-orange-500 text-orange-600 px-8 py-3 rounded-full text-lg font-semibold hover:bg-orange-500 hover:text-white transition-all">
                Book a free demo
              </button>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=600&fit=crop"
                alt="Sales Team"
                className="rounded-3xl shadow-2xl hover-lift"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Smarter Engagement Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <img
                src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800&h=600&fit=crop"
                alt="Global Network"
                className="rounded-3xl shadow-2xl hover-lift"
              />
            </div>
            <div className="order-1 md:order-2">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Smarter Engagement,{' '}
                <span className="text-gradient">Faster Closures</span>
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-8">
                Whether you sell Home or cars, your buyers expect instant responses. Our bots handle the first contact, qualify serious clients, and route them to your team automatically.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0 font-bold text-gray-600">
                    01
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-1">Respond To Every Inquiry In Second</h4>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0 font-bold text-gray-600">
                    02
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-1">Book Viewings Or Test Drives Automatically</h4>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0 font-bold text-gray-600">
                    03
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-1">Sync Leads To Airtable, HubSpot, Or Sheets</h4>
                  </div>
                </div>
              </div>
              <button onClick={() => {
                setModalButtonType('automate');
                setIsSignupModalOpen(true);
              }} className="bg-gray-800 text-white px-8 py-3 rounded-lg text-lg font-semibold hover:bg-gray-900 transition-all">
                Book a free demo
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Core Features Banner */}
      <section className="py-20 px-4 bg-gradient-to-br from-slate-800 via-purple-900 to-slate-800 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-10 left-10 w-32 h-32 bg-white rounded-full blur-3xl animate-pulse-slow"></div>
          <div className="absolute bottom-10 right-10 w-40 h-40 bg-purple-600 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '1s' }}></div>
        </div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">Qorvix Core Features</h2>
          <p className="text-lg opacity-90 mb-8">Everything your team needs to automate lead response, follow-up, and conversion.</p>
          <div className="flex justify-center gap-0 mb-8 -space-x-4">
            <img
              src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop"
              alt="Team member"
              className="w-20 h-20 rounded-full border-4 border-slate-800"
            />
            <img
              src="https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=100&h=100&fit=crop"
              alt="Team member"
              className="w-20 h-20 rounded-full border-4 border-slate-800"
            />
            <img
              src="https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop"
              alt="Team member"
              className="w-20 h-20 rounded-full border-4 border-slate-800"
            />
            <img
              src="https://images.unsplash.com/photo-1519345182560-3f2917c472ef?w=100&h=100&fit=crop"
              alt="Team member"
              className="w-20 h-20 rounded-full border-4 border-slate-800"
            />
            <img
              src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop"
              alt="Team member"
              className="w-20 h-20 rounded-full border-4 border-slate-800"
            />
          </div>
          <div className="text-6xl md:text-7xl font-bold mb-2">15,000+</div>
          <p className="text-xl opacity-90">Leads Automated Daily</p>
        </div>
      </section>

      {/* Powerful Features Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Powerful Features</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Everything you need to build smarter, faster, AI-driven workflows — built for real business operations, not just demos.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => {
              const IconComponent = feature.Icon;
              const isTopRow = index < 4;
              return (
                <div
                  key={index}
                  className="bg-gray-50 p-8 rounded-2xl text-center hover:shadow-xl transition-all hover-lift"
                >
                  <div className={`w-20 h-20 ${isTopRow && index % 2 === 1 ? 'bg-gray-600' : 'bg-gray-400'} rounded-full flex items-center justify-center mx-auto mb-6`}>
                    <IconComponent className="text-white" size={32} strokeWidth={2} />
                  </div>
                  <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>


      {/* Pricing Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Choose Your <span className="text-gradient">Automation Plan</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              From simple lead bots to full-scale AI systems — Qorvix helps your business scale smarter, faster, and 24/7.
            </p>
          </div>

          {/* Billing toggle */}
          <div className="flex items-center justify-center gap-4 mb-12">
            <span className={`font-medium ${!isAnnualBilling ? 'text-gray-900' : 'text-gray-500'}`}>Monthly</span>
            <button
              onClick={() => setIsAnnualBilling(!isAnnualBilling)}
              className={`relative w-14 h-7 rounded-full transition-colors ${isAnnualBilling ? 'bg-orange-500' : 'bg-gray-300'}`}
              aria-label="Toggle annual billing"
            >
              <span
                className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full shadow-md transition-transform ${isAnnualBilling ? 'translate-x-7' : 'translate-x-0'}`}
              ></span>
            </button>
            <span className={`font-medium ${isAnnualBilling ? 'text-gray-900' : 'text-gray-500'}`}>Annually</span>
            <span className="text-orange-500 text-sm font-semibold">(Save up to 20%)</span>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Starter */}
            <div className="bg-white border border-gray-200 rounded-2xl p-8 flex flex-col hover:shadow-xl transition-all">
              <h3 className="text-lg font-semibold text-gray-700 mb-3">Starter</h3>
              <div className="mb-3">
                <span className="text-5xl font-bold">£{isAnnualBilling ? '21.59' : '26.99'}</span>
                <span className="text-gray-500 ml-1">/mo</span>
              </div>
              <p className="text-gray-600 text-sm mb-6">For sales teams to manage conversations and achieve goals.</p>
              <div className="border-t border-gray-200 pt-4 mb-4 space-y-2 text-sm">
                <div className="flex justify-between text-gray-600"><span>Conversations</span><span className="font-medium text-gray-900">500/mo</span></div>
                <div className="flex justify-between text-gray-600"><span>Team members</span><span className="font-medium text-gray-900">3</span></div>
                <div className="flex justify-between text-gray-600"><span>Platforms</span><span className="font-medium text-gray-900">1 platform</span></div>
              </div>
              <ul className="space-y-3 text-sm text-gray-700 mb-8 flex-grow">
                {[
                  '1 platform of your choice',
                  '500 conversations/month',
                  '1 AI Agent (templates only)',
                  'Basic auto-replies + lead scoring',
                  'Knowledge base (5 uploads)',
                  'Basic analytics',
                  '3 team members'
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <a
                href="http://app.qorvix.ai/"
                className="w-full border-2 border-gray-300 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-all text-center"
              >
                Subscribe Now
              </a>
            </div>

            {/* Growth - Most popular */}
            <div className="bg-white border-2 border-orange-500 rounded-2xl p-8 flex flex-col relative shadow-xl">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                <span className="bg-orange-500 text-white text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-wide">Most popular</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-700 mb-3">Growth</h3>
              <div className="mb-3">
                <span className="text-5xl font-bold">£{isAnnualBilling ? '55' : '69'}</span>
                <span className="text-gray-500 ml-1">/mo</span>
              </div>
              <p className="text-gray-600 text-sm mb-6">For marketing and sales teams looking to maximize conversions with AI.</p>
              <div className="border-t border-gray-200 pt-4 mb-4 space-y-2 text-sm">
                <div className="flex justify-between text-gray-600"><span>Conversations</span><span className="font-medium text-gray-900">2,000/mo</span></div>
                <div className="flex justify-between text-gray-600"><span>Team members</span><span className="font-medium text-gray-900">10</span></div>
                <div className="flex justify-between text-gray-600"><span>Platforms</span><span className="font-medium text-gray-900">2 platforms</span></div>
              </div>
              <p className="text-sm font-semibold text-gray-900 mb-3">Everything in Starter, plus:</p>
              <ul className="space-y-3 text-sm text-gray-700 mb-8 flex-grow">
                {[
                  '2 platforms',
                  '2,000 conversations/month',
                  'Unlimited AI Agents (custom)',
                  'Workflows (6 templates + custom)',
                  'Unlimited knowledge base',
                  'Calendar & bookings',
                  'Integrations (Slack, Zapier, HubSpot)',
                  'Priority support',
                  '10 team members'
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <a
                href="http://app.qorvix.ai/"
                className="w-full bg-orange-500 text-white py-3 rounded-lg font-semibold hover:bg-orange-600 transition-all shadow-md text-center"
              >
                Subscribe Now
              </a>
            </div>

            {/* Scale */}
            <div className="bg-white border border-gray-200 rounded-2xl p-8 flex flex-col hover:shadow-xl transition-all">
              <h3 className="text-lg font-semibold text-gray-700 mb-3">Scale</h3>
              <div className="mb-3">
                <span className="text-5xl font-bold">£{isAnnualBilling ? '119' : '149'}</span>
                <span className="text-gray-500 ml-1">/mo</span>
              </div>
              <p className="text-gray-600 text-sm mb-6">For teams that need all platforms, advanced AI, and security.</p>
              <div className="border-t border-gray-200 pt-4 mb-4 space-y-2 text-sm">
                <div className="flex justify-between text-gray-600"><span>Conversations</span><span className="font-medium text-gray-900">Unlimited/mo</span></div>
                <div className="flex justify-between text-gray-600"><span>Team members</span><span className="font-medium text-gray-900">25</span></div>
                <div className="flex justify-between text-gray-600"><span>Platforms</span><span className="font-medium text-gray-900">All platforms</span></div>
              </div>
              <p className="text-sm font-semibold text-gray-900 mb-3">Everything in Growth, plus:</p>
              <ul className="space-y-3 text-sm text-gray-700 mb-8 flex-grow">
                {[
                  'All platforms (WhatsApp + Instagram + FB + Email)',
                  'Unlimited conversations',
                  'Priority AI (faster, longer context)',
                  'Custom prompts & tone settings',
                  'Multiple WhatsApp numbers',
                  'API access + webhooks',
                  'Inventory with image sending',
                  'Dedicated account manager',
                  '25 team members'
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <a
                href="http://app.qorvix.ai/"
                className="w-full border-2 border-gray-300 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-all text-center"
              >
                Subscribe Now
              </a>
            </div>

            {/* Enterprise / Custom */}
            <div className="bg-slate-900 text-white rounded-2xl p-8 flex flex-col relative">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                <span className="bg-slate-900 text-white text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-wide border border-slate-700">Enterprise</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-300 mb-3">Custom</h3>
              <div className="mb-3">
                <span className="text-5xl font-bold">Custom</span>
              </div>
              <p className="text-gray-400 text-sm mb-2">£1,500 one-time setup</p>
              <p className="text-gray-300 text-sm mb-6">Bespoke solution with custom integrations and AI fine-tuning.</p>
              <div className="border-t border-slate-700 pt-4 mb-4 space-y-2 text-sm">
                <div className="flex justify-between text-gray-400"><span>Conversations</span><span className="font-medium text-white">Unlimited/mo</span></div>
                <div className="flex justify-between text-gray-400"><span>Team members</span><span className="font-medium text-white">Unlimited</span></div>
                <div className="flex justify-between text-gray-400"><span>Platforms</span><span className="font-medium text-white">All + custom</span></div>
              </div>
              <p className="text-sm font-semibold text-white mb-3">Everything in Scale, plus:</p>
              <ul className="space-y-3 text-sm text-gray-300 mb-8 flex-grow">
                {[
                  'Custom conversation flows',
                  'Custom platform integrations',
                  'Custom AI fine-tuning on your data',
                  'White-glove onboarding & training',
                  'SLA & uptime guarantee',
                  'On-premise deployment option',
                  'Unlimited everything'
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <a
                href="http://app.qorvix.ai/"
                className="w-full bg-white text-slate-900 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-all text-center"
              >
                Contact Sales
              </a>
            </div>
          </div>

          <p className="text-center text-sm text-gray-500 mt-10">
            7-day free trial on all plans. Cancel anytime. Platform options: WhatsApp, Instagram, Facebook Messenger, Email.<br />
            All prices in GBP. Billed monthly or annually.
          </p>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">What Our Clients Say</h2>
            <p className="text-lg opacity-90 max-w-3xl mx-auto">
              Real results from businesses that automated with Qorvix
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-white/10 backdrop-blur-md p-8 rounded-2xl border border-white/20 hover-lift"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="text-yellow-400 fill-yellow-400" size={20} />
                  ))}
                </div>
                <p className="text-white/90 mb-6 leading-relaxed italic">"{testimonial.text}"</p>
                <div>
                  <div className="font-bold text-lg">{testimonial.name}</div>
                  <div className="text-sm text-white/70">{testimonial.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Media & Publications Section */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Featured In <span className="text-gradient">Leading Publications</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our automation expertise and industry insights have been recognized by top business publications
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Publication 1 */}
            <a
              href="https://sunnews.ng/qorvix-launches-automation-platform-to-help-businesses-run-customer-conversations-as-a-system/
"
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-gradient-to-br from-orange-50 to-white p-8 rounded-2xl border-2 border-orange-100 hover:border-orange-300 transition-all hover:shadow-xl"
            >
              <div className="flex items-start gap-6">
                <div className="flex-shrink-0">
                  <div className="w-20 h-20 bg-white rounded-lg shadow-md flex items-center justify-center">
                    <img
                      src="https://i.ibb.co/1G362knq/publication-logo-1.png"
                      alt="Publication Logo"
                      className="w-16 h-16 object-contain"
                    />
                  </div>
                </div>
                <div className="flex-1">
                  <span className="text-xs font-semibold text-orange-600 uppercase tracking-wide mb-2 block">
                    Featured Article
                  </span>
                  <h3 className="text-xl font-bold mb-2 group-hover:text-orange-500 transition-colors">
                    Qorvix Launches Automation Platform to Help Businesses Run Customer Conversations as a System
                  </h3>
                  <p className="text-gray-600 text-sm mb-3">
                    Qorvix, a new business automation platform, has launched with a clear mission: to help businesses manage customer conversations as a scalable system rather than a series of manual, fragmented replies.
                  </p>
                  <div className="flex items-center text-orange-500 font-semibold text-sm group-hover:translate-x-2 transition-transform">
                    Read Full Article →
                  </div>
                </div>
              </div>
            </a>

            {/* Publication 2 */}
            <a
              href="https://standardmirror.ng/qorvix-unveils-new-system-to-automate-and-streamline-customer-communication/"
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-gradient-to-br from-purple-50 to-white p-8 rounded-2xl border-2 border-purple-100 hover:border-purple-300 transition-all hover:shadow-xl"
            >
              <div className="flex items-start gap-6">
                <div className="flex-shrink-0">
                  <div className="w-20 h-20 bg-white rounded-lg shadow-md flex items-center justify-center">
                    <img
                      src="https://i.ibb.co/gbzkdRbG/publication-logo-2.png"
                      alt="Publication Logo"
                      className="w-16 h-16 object-contain"
                    />
                  </div>
                </div>
                <div className="flex-1">
                  <span className="text-xs font-semibold text-purple-600 uppercase tracking-wide mb-2 block">
                    Industry Insights
                  </span>
                  <h3 className="text-xl font-bold mb-2 group-hover:text-purple-500 transition-colors">
                    Qorvix Unveils New System to Automate and Streamline Customer Communication
                  </h3>
                  <p className="text-gray-600 text-sm mb-3">
                    Qorvix has entered the market with a platform designed to help businesses transform customer communication into an organized, scalable system instead of relying on scattered manual responses.
                  </p>
                  <div className="flex items-center text-purple-500 font-semibold text-sm group-hover:translate-x-2 transition-transform">
                    Read Full Article →
                  </div>
                </div>
              </div>
            </a>
          </div>

          {/* Optional: Media Logos Row */}
          {/* <div className="mt-16 pt-12 border-t border-gray-200">
            <p className="text-center text-gray-500 text-sm mb-8 uppercase tracking-wide font-semibold">
              As Seen In
            </p>
            <div className="flex flex-wrap justify-center items-center gap-12 opacity-60">
              {/* Add actual publication logos here  
              <img src="/logo-forbes.png" alt="Forbes" className="h-8 grayscale hover:grayscale-0 transition-all" />
              <img src="/logo-techcrunch.png" alt="TechCrunch" className="h-8 grayscale hover:grayscale-0 transition-all" />
              <img src="/logo-entrepreneur.png" alt="Entrepreneur" className="h-8 grayscale hover:grayscale-0 transition-all" />
              <img src="/logo-business-insider.png" alt="Business Insider" className="h-8 grayscale hover:grayscale-0 transition-all" />
            </div>
          </div> */}
        </div>
      </section>
      {/* Contact Section */}
      <section id="contact" className="py-20 px-4 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Contact Us</h2>
            <p className="text-lg text-gray-600">We are here for you! How can we help?</p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <div className="bg-white p-8 rounded-2xl shadow-xl">
                <h3 className="text-2xl font-bold mb-6">Get In Touch</h3>
                <form onSubmit={handleContactSubmit} className="space-y-6">
                  {/* Success Message */}
                  {contactSuccess && (
                    <div className="bg-green-50 border-2 border-green-500 text-green-800 px-6 py-4 rounded-lg">
                      ✅ Message sent successfully! We'll get back to you soon.
                    </div>
                  )}

                  {/* Username */}
                  <div>
                    <input
                      type="text"
                      name="username"
                      value={contactFormData.username}
                      onChange={handleContactChange}
                      placeholder="Enter username here"
                      required
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-orange-500 focus:outline-none transition-colors"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <input
                      type="email"
                      name="email"
                      value={contactFormData.email}
                      onChange={handleContactChange}
                      placeholder="Enter your email address"
                      required
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-orange-500 focus:outline-none transition-colors"
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <input
                      type="tel"
                      name="phone"
                      value={contactFormData.phone}
                      onChange={handleContactChange}
                      placeholder="Phone number (Optional)"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-orange-500 focus:outline-none transition-colors"
                    />
                  </div>

                  {/* Service Dropdown */}
                  <div>
                    <select
                      name="service"
                      value={contactFormData.service}
                      onChange={handleContactChange}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-orange-500 focus:outline-none transition-colors appearance-none bg-white"
                    >
                      <option value="">How can we help</option>
                      <option value="general">General Inquiry</option>
                      <option value="demo">Request Demo</option>
                      <option value="support">Technical Support</option>
                      <option value="sales">Sales Question</option>
                      <option value="partnership">Partnership</option>
                    </select>
                  </div>

                  {/* Message */}
                  <div>
                    <textarea
                      name="message"
                      value={contactFormData.message}
                      onChange={handleContactChange}
                      placeholder="Go ahead, we are listening..."
                      rows="4"
                      required
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-orange-500 focus:outline-none transition-colors resize-none"
                    ></textarea>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isContactSubmitting}
                    className="w-full bg-orange-500 text-white py-4 rounded-lg font-semibold hover:bg-orange-600 transition-all disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {isContactSubmitting ? 'Sending...' : 'Submit'}
                  </button>
                </form>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-white p-6 rounded-2xl shadow-lg hover-lift">
                <div className="flex items-start gap-4">
                  <div className="bg-orange-100 p-3 rounded-full">
                    <Mail className="text-orange-600" size={24} />
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-1">Email Us</h4>
                    <p className="text-gray-600">Support@qorvix.ai</p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-2xl shadow-lg hover-lift">
                <div className="flex items-start gap-4">
                  <div className="bg-orange-100 p-3 rounded-full">
                    <Phone className="text-orange-600" size={24} />
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-1">Call Us</h4>
                    <p className="text-gray-600">+44 7757 233260</p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-2xl shadow-lg hover-lift">
                <div className="flex items-start gap-4">
                  <div className="bg-orange-100 p-3 rounded-full">
                    <MapPin className="text-orange-600" size={24} />
                  </div>
                  <div>
                    <h4 className="font-bold text-lg mb-1">Our Office</h4>
                    <p className="text-gray-600">124 City Road, London, EC1V 2NX</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-400 via-orange-300 to-purple-400"></div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-800">
            Access Your Business Automation Potential Today & Unlock Smarter Growth
          </h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
            <button onClick={() => {
              setModalButtonType('automate');
              setIsSignupModalOpen(true);
            }} className="bg-gray-800 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-900 transition-all">
              Talk To Automation Expert
            </button>
            <button className="border-2 border-gray-800 text-gray-800 bg-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-50 transition-all">
              See Prices & Plans
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white py-12 px-4 border-t border-gray-200">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-xl font-bold mb-4">
                <span className="text-gradient">Qorvix</span> — Automate What Matters
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed mb-6">
                Qorvix helps businesses automate workflows, connect tools, and scale faster using AI-powered systems. From CRM integrations to WhatsApp automations — we build smart, no-code solutions that work.
              </p>
              <div className="flex gap-3">
                <a href="" className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center hover:bg-gray-300 transition-colors">
                  <X size={18} className="text-gray-700" />
                </a>
                <a href="" className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center hover:bg-gray-300 transition-colors">
                  <Mail size={18} className="text-gray-700" />
                </a>
                <a href="" className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center hover:bg-gray-300 transition-colors">
                  <svg className="w-5 h-5 text-gray-700" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                  </svg>
                </a>
              </div>
            </div>

            <div>
              <h4 className="font-bold mb-4">Contact</h4>
              <div className="space-y-2 text-sm text-gray-600">
                <p>London, England, UK</p>
                <p>+44 7757 233260</p>
                <p>Support@qorvix.ai</p>
              </div>
            </div>

            <div>
              <h4 className="font-bold mb-4">Company</h4>
              <div className="space-y-2 text-sm">
                <Link to="/" className="block text-gray-600 hover:text-gray-900 transition-colors">Home</Link>
                <Link to="/about" className="block text-gray-600 hover:text-gray-900 transition-colors">About</Link>
                <Link to="/portfolio" className="block text-gray-600 hover:text-gray-900 transition-colors">Portfolio</Link>
                <Link to="/contact" className="block text-gray-600 hover:text-gray-900 transition-colors">Contact</Link>
                <Link to="/how-it-works" className="block text-gray-600 hover:text-gray-900 transition-colors">How it works</Link>
              </div>
            </div>

            <div>
              <h4 className="font-bold mb-4">Resources</h4>
              <div className="space-y-2 text-sm">
                <Link to="/documentation" className="block text-gray-600 hover:text-gray-900 transition-colors">
                  Documentation
                </Link>

                <Link to="/integrations" className="block text-gray-600 hover:text-gray-900 transition-colors">
                  Integration
                </Link>

                <Link to="/faq" className="block text-gray-600 hover:text-gray-900 transition-colors">
                  FAQs
                </Link>

                <Link to="/help-center" className="block text-gray-600 hover:text-gray-900 transition-colors">
                  Help Center
                </Link>

                <Link to="/roadmap" className="block text-gray-600 hover:text-gray-900 transition-colors">
                  Roadmap
                </Link>
              </div>
            </div>
          </div>

          <div className="pt-8 flex items-center justify-between border-t border-gray-200">
            <div className="text-2xl font-bold text-gradient">Qorvix</div>
            <div className="text-sm text-gray-600">
              © Qorvix Technologies. All Rights Reserved. Built with purpose and precision by <span className="font-semibold">Qorvix Team</span>.
            </div>
          </div>
        </div>
      </footer>

      {/* Signup Modal */}
      <SignupFormModal
        isOpen={isSignupModalOpen}
        onClose={() => setIsSignupModalOpen(false)}
        buttonType={modalButtonType}
      />
    </div>
  );
}